import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Search, Plus, Filter, CheckCircle2, Circle, Pencil, Trash2, ImageIcon, Calendar, X } from 'lucide-react';
import api from '../utils/api';
import AddCollectionModal from '../components/AddCollectionModal';
import ExportExcelButton from '../components/ExportExcelButton';
import ImageModal from '../components/ImageModal';
import { formatRecordDate, formatRecordTime, getLocalDateInputValue } from '../utils/dateUtils';
import toast from 'react-hot-toast';

// Satıcı / Alıcı / Kurum açılır filtresi. Seçenekler mevcut kayıtlardan gelir ("Ad (kayıt sayısı)").
// Backend (SQLite COLLATE NOCASE) yalnızca ASCII harfleri katlar. Tarayıcıdaki toLocaleLowerCase('tr-TR')
// "I"yı "ı" yaptığı için eşleşme sunucudan farklı çıkardı; aynı kuralı burada da uyguluyoruz.
const foldAscii = (text) => text.replace(/[A-Z]/g, (char) => char.toLowerCase());

const FilterSelect = ({ label, allLabel, value, onChange, options }) => {
    const sorted = [...options].sort((a, b) => a.value.localeCompare(b.value, 'tr'));
    // Backend eşleşmesi büyük-küçük harfe bakmaz; liste de aynı kuralla seçili seçeneği bulur
    const matchedOption = value ? sorted.find((option) => foldAscii(option.value) === foldAscii(value.trim())) : null;
    // Seçili değer artık hiçbir kayıtta yoksa (düzenlendi/silindi) seçim kaybolmasın
    const hasSelectedOption = !value || Boolean(matchedOption);

    return (
        <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-dark-muted mb-1">{label}</label>
            <div className="relative">
                <select
                    value={matchedOption ? matchedOption.value : value}
                    onChange={(e) => onChange(e.target.value)}
                    aria-label={`${label} filtresi`}
                    className={`appearance-none w-full pl-10 pr-4 py-2.5 bg-dark-bg/50 border rounded-lg text-dark-text focus:outline-none focus:border-primary-500 transition-colors cursor-pointer shadow-sm truncate ${value ? 'border-primary-500' : 'border-dark-border'}`}
                >
                    <option value="">{allLabel}</option>
                    {!hasSelectedOption && <option value={value}>{value}</option>}
                    {sorted.map((option) => (
                        <option key={option.value} value={option.value}>{option.value} ({option.count})</option>
                    ))}
                </select>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-dark-muted">
                    <Filter size={18} />
                </div>
            </div>
        </div>
    );
};

const Collections = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const canEditCollections = user?.role === 'admin';
    const [collections, setCollections] = useState([]);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
    const [accountingFilter, setAccountingFilter] = useState(searchParams.get('accountingStatus') || '');
    const [sellerFilter, setSellerFilter] = useState(searchParams.get('seller') || '');
    const [buyerFilter, setBuyerFilter] = useState(searchParams.get('buyer') || '');
    const [institutionFilter, setInstitutionFilter] = useState(searchParams.get('institution') || '');
    const [filterOptions, setFilterOptions] = useState({ sellers: [], buyers: [], institutions: [] });
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState(getLocalDateInputValue());
    const [isLoading, setIsLoading] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [collectionToEdit, setCollectionToEdit] = useState(null);
    const [viewImage, setViewImage] = useState(null);

    // Sayfalama
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50, totalPages: 0 });
    const scrollSentinelRef = useRef(null);

    // Satıcı / alıcı / kurum seçenekleri veritabanındaki mevcut değerlerden gelir
    const fetchFilterOptions = useCallback(async () => {
        try {
            const response = await api.get('/collections/filter-options');
            setFilterOptions({
                sellers: response.data.sellers || [],
                buyers: response.data.buyers || [],
                institutions: response.data.institutions || []
            });
        } catch (error) {
            // Seçenekler yüklenemezse sayfa yine de çalışır (arama kutusu ve diğer filtreler açık kalır)
            console.error('Filtre seçenekleri alınamadı', error);
        }
    }, []);

    const fetchCollections = useCallback(async (pageNum = 1, append = false) => {
        setIsLoading(true);
        try {
            const response = await api.get('/collections', {
                params: {
                    search, seller: sellerFilter, buyer: buyerFilter, institution: institutionFilter,
                    status: statusFilter, accountingStatus: accountingFilter, startDate, endDate, page: pageNum, limit: 20
                }
            });
            // Backend artık { data, pagination } döndürüyor
            if (response.data.data) {
                setCollections(prev => append ? [...prev, ...response.data.data] : response.data.data);
                setPagination(response.data.pagination);
            } else {
                // Geriye uyumluluk
                setCollections(response.data);
            }
        } catch (error) {
            console.error("Kayıtlar çekilemedi", error);
            toast.error("Kayıtlar yüklenemedi.");
        } finally {
            setIsLoading(false);
        }
    }, [search, sellerFilter, buyerFilter, institutionFilter, statusFilter, accountingFilter, startDate, endDate]);

    const loadAllCollectionsForExport = useCallback(async () => {
        const response = await api.get('/collections', {
            params: {
                search,
                seller: sellerFilter,
                buyer: buyerFilter,
                institution: institutionFilter,
                status: statusFilter,
                accountingStatus: accountingFilter,
                startDate,
                endDate,
                page: 1,
                limit: 100000,
            },
        });
        return response.data.data || response.data;
    }, [search, sellerFilter, buyerFilter, institutionFilter, statusFilter, accountingFilter, startDate, endDate]);

    useEffect(() => {
        if (user?.role === 'shipment_staff') return;
        fetchFilterOptions();
    }, [fetchFilterOptions, user?.role]);

    useEffect(() => {
        if (user?.role === 'shipment_staff') {
            navigate('/shipments');
            return;
        }

        const delayDebounceFn = setTimeout(() => {
            fetchCollections(1, false);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [fetchCollections, user?.role, navigate]);

    // Infinite Scroll — IntersectionObserver ile (main scroll container uyumlu)
    const loadMore = useCallback(() => {
        if (!isLoading && pagination.page < pagination.totalPages) {
            fetchCollections(pagination.page + 1, true);
        }
    }, [fetchCollections, isLoading, pagination.page, pagination.totalPages]);

    useEffect(() => {
        const sentinel = scrollSentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMore();
                }
            },
            { rootMargin: '200px' }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loadMore]);

    // Durum Güncelleme (Çift Tik)
    const handleToggle = async (id, field, currentValue) => {
        if (!canEditCollections) {
            toast.error('Bu işlem için yetkiniz bulunmuyor.');
            return;
        }

        try {
            const newValue = currentValue === 1 ? 0 : 1;
            await api.patch(`/collections/${id}/status`, {
                [field]: newValue
            });
            toast.success(field === 'isCollected' ? 'Tahsilat durumu güncellendi' : 'Muhasebe durumu güncellendi');
            const activeFilter = field === 'isCollected' ? statusFilter : accountingFilter;
            if (activeFilter !== '' && Number(activeFilter) !== newValue) {
                // Kayıt artık aktif filtreye uymuyorsa listeyi ve sayacı sunucudan tazele.
                fetchCollections(1, false);
            } else {
                setCollections(prev => prev.map(c => c.id === id ? { ...c, [field]: newValue } : c));
            }
        } catch (error) {
            console.error("Durum güncellenemedi", error);
            if (error.response?.status === 403) {
                toast.error("Bu işlem için yetkiniz bulunmuyor.");
            } else {
                toast.error("Durum güncellenemedi!");
            }
        }
    };

    const handleDelete = async (id) => {
        if (!canEditCollections) {
            toast.error('Kayıt silme yetkiniz yok.');
            return;
        }

        if (window.confirm('Bu tahsilat kaydını kalıcı olarak silmek istediğinize emin misiniz?')) {
            try {
                await api.delete(`/collections/${id}`);
                toast.success('Kayıt başarıyla silindi.');
                setCollections(prev => prev.filter(c => c.id !== id));
                fetchFilterOptions();
            } catch (error) {
                if (error.response?.status === 403) {
                    toast.error("Kayıt silme yetkiniz yok.");
                } else {
                    toast.error("Silme işlemi başarısız.");
                }
            }
        }
    };

    // Para biçimlendirme
    const formatAmount = (amount) => {
        return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    };

    return (
        <Layout>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-xl md:text-3xl font-bold text-dark-text mb-2 tracking-tight">Tahsilat Kayıtları</h1>
                    <p className="text-dark-muted text-sm md:text-base">Tüm alacak verilerini incele, filtrele veya yeni kayıt ekle.</p>
                </div>

                <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
                    <ExportExcelButton data={collections} loadData={loadAllCollectionsForExport} />
                    {canEditCollections && (
                        <button
                            onClick={() => { setCollectionToEdit(null); setIsAddModalOpen(true); }}
                            className="w-full md:w-auto flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-500 text-white px-5 py-3 rounded-xl font-bold tracking-wide transition-all shadow-lg shadow-primary-500/20 hover:scale-105 active:scale-95"
                        >
                            <Plus size={20} className="stroke-[3]" />
                            <span>YENI KAYIT EKLE</span>
                        </button>
                    )}
                </div>
            </div>

            {/* MODAL */}
            {canEditCollections && (
                <AddCollectionModal
                    isOpen={isAddModalOpen}
                    editData={collectionToEdit}
                    existingCollections={collections}
                    onClose={() => { setIsAddModalOpen(false); setCollectionToEdit(null); }}
                    onRefresh={({ resetToFirstPage } = {}) => {
                        fetchCollections(resetToFirstPage ? 1 : pagination.page, false);
                        fetchFilterOptions();
                    }}
                />
            )}

            {/* ARAMA VE FİLTRELEME ÇUBUĞU */}
            <div className="glass p-4 rounded-xl mb-6 flex flex-col gap-4 border border-dark-border relative z-10">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-dark-muted">
                        <Search size={18} />
                    </div>
                    <input
                        type="text"
                        placeholder="Satıcı, Alıcı, Not veya Transfer No Ara..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-11 pr-4 py-2.5 bg-dark-bg/50 border border-dark-border rounded-lg text-dark-text placeholder:text-dark-muted focus:outline-none focus:border-primary-500 transition-colors shadow-sm"
                    />
                </div>

                <div className="flex flex-wrap gap-2">
                    {/* Durum Filtresi */}
                    <div className="relative">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="appearance-none w-full md:w-48 pl-10 pr-4 py-2.5 bg-dark-bg/50 border border-dark-border rounded-lg text-dark-text focus:outline-none focus:border-primary-500 transition-colors cursor-pointer shadow-sm"
                        >
                            <option value="">Tümü</option>
                            <option value="0">Bekleyenler</option>
                            <option value="1">Tamamlananlar</option>
                        </select>
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-dark-muted">
                            <Filter size={18} />
                        </div>
                    </div>

                    <div className="relative">
                        <select
                            value={accountingFilter}
                            onChange={(e) => setAccountingFilter(e.target.value)}
                            aria-label="Muhasebe durumu"
                            className="appearance-none w-full md:w-56 pl-10 pr-4 py-2.5 bg-dark-bg/50 border border-dark-border rounded-lg text-dark-text focus:outline-none focus:border-primary-500 transition-colors cursor-pointer shadow-sm"
                        >
                            <option value="">Tüm Muhasebe Durumları</option>
                            <option value="0">Muhasebeleştirilmemiş</option>
                            <option value="1">Muhasebeleştirilmiş</option>
                        </select>
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-dark-muted">
                            <Filter size={18} />
                        </div>
                    </div>

                    {/* Tarih Filtresi */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                        <div className="relative flex-1 sm:flex-none">
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-full sm:w-auto pl-9 pr-3 py-2.5 bg-dark-bg/50 border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-primary-500 cursor-pointer shadow-sm"
                            />
                            <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted pointer-events-none" />
                        </div>
                        <span className="text-dark-muted text-xs hidden sm:inline">—</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full sm:w-auto px-3 py-2.5 bg-dark-bg/50 border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-primary-500 cursor-pointer shadow-sm"
                        />
                        {(startDate || endDate) && (
                            <button onClick={() => { setStartDate(''); setEndDate(''); }} className="text-xs text-red-500 hover:text-red-600 px-2 whitespace-nowrap font-medium transition-colors">Temizle</button>
                        )}
                    </div>
                </div>
              </div>

              {/* SATICI / ALICI / KURUM FİLTRELERİ */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                <FilterSelect label="Satıcı" allLabel="Tüm Satıcılar" value={sellerFilter} onChange={setSellerFilter} options={filterOptions.sellers} />
                <FilterSelect label="Alıcı" allLabel="Tüm Alıcılar" value={buyerFilter} onChange={setBuyerFilter} options={filterOptions.buyers} />
                <FilterSelect label="Kurum" allLabel="Tüm Kurumlar" value={institutionFilter} onChange={setInstitutionFilter} options={filterOptions.institutions} />
              </div>
              {(sellerFilter || buyerFilter || institutionFilter) && (
                <button
                    onClick={() => { setSellerFilter(''); setBuyerFilter(''); setInstitutionFilter(''); }}
                    className="self-start inline-flex items-center gap-1.5 text-xs font-medium text-red-500 hover:text-red-600 transition-colors"
                >
                    <X size={14} /> Satıcı / alıcı / kurum filtrelerini temizle
                </button>
              )}
            </div>

            {/* VERİ TABLOSU */}
            <div className="glass border border-dark-border rounded-2xl overflow-hidden shadow-2xl">
                <div className="mobile-table-hint lg:hidden">Tüm alanlar ve işlemler için tabloyu yatay kaydırın →</div>
                <div className="mobile-table-scroll overflow-x-auto">
                    <table className="mobile-complete-table w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-dark-card text-dark-muted border-b border-dark-border uppercase tracking-wider text-[11px] md:text-xs">
                            <tr>
                                <th className="px-3 md:px-6 py-4 font-semibold">Satıcı</th>
                                <th className="px-3 md:px-6 py-4 font-semibold hidden sm:table-cell">Alıcı</th>
                                <th className="px-3 md:px-6 py-4 font-semibold">Kurum/Ref</th>
                                <th className="px-3 md:px-6 py-4 font-semibold">Tutar</th>
                                <th className="px-3 md:px-6 py-4 font-semibold w-12 text-center">Durum</th>
                                <th className="px-3 md:px-6 py-4 font-semibold hidden md:table-cell">Muhasebe</th>
                                <th className="px-3 md:px-6 py-4 font-semibold hidden lg:table-cell">Tarih</th>
                                <th className="px-3 md:px-6 py-4 font-semibold text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-dark-border">
                            {isLoading && collections.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-12 text-center text-dark-muted">
                                        <div className="inline-block w-6 h-6 border-2 border-dark-muted border-t-transparent rounded-full animate-spin mb-2"></div>
                                        <p>Veriler yükleniyor...</p>
                                    </td>
                                </tr>
                            ) : collections.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-12 text-center text-dark-muted font-medium">
                                        Eşleşen herhangi bir döviz tahsilat kaydı bulunamadı.
                                    </td>
                                </tr>
                            ) : (
                                collections.map((row) => (
                                    <tr key={row.id} className="hover:bg-dark-hover transition-colors">

                                        {/* SATICI */}
                                        <td className="px-3 md:px-6 py-4">
                                            <div className="font-semibold text-dark-text text-xs md:text-sm">{row.seller}</div>
                                        </td>

                                        {/* ALICI */}
                                        <td className="px-3 md:px-6 py-4 hidden sm:table-cell">
                                            <div className="font-semibold text-dark-text text-xs md:text-sm">{row.buyer}</div>
                                            {row.notes && <div className="text-xs text-dark-muted mt-1 max-w-[150px] truncate" title={row.notes}>{row.notes}</div>}
                                        </td>

                                        {/* KURUM */}
                                        <td className="px-3 md:px-6 py-4">
                                            <span className="inline-flex items-center px-1.5 py-0.5 md:px-2 md:py-1 rounded bg-dark-card text-[10px] md:text-xs font-medium text-dark-text border border-dark-border shadow-sm">
                                                {row.institution}
                                            </span>
                                            {row.transferNo && <div className="text-[10px] md:text-xs text-dark-muted mt-1 font-mono">Ref: {row.transferNo}</div>}
                                        </td>

                                        {/* TUTAR + KISMİ TAHSİLAT */}
                                        <td className="px-3 md:px-6 py-4">
                                            <div className="font-bold text-sm md:text-lg text-dark-text">
                                                {formatAmount(row.remainingAmount !== null && row.remainingAmount < row.amount ? row.remainingAmount : row.amount)} <span className="text-xs md:text-sm font-medium text-dark-muted">{row.currency}</span>
                                            </div>
                                            {/* Kısmi tahsilat yapıldıysa asıl tutarı küçük göster */}
                                            {row.remainingAmount !== null && row.remainingAmount > 0 && row.remainingAmount < row.amount && (
                                                <div className="text-[10px] md:text-xs text-orange-500 mt-0.5 font-medium">
                                                    Asıl: {formatAmount(row.amount)} {row.currency}
                                                </div>
                                            )}
                                        </td>

                                        {/* TAHSİLAT DURUMU */}
                                        <td className="px-3 md:px-6 py-4 text-center">
                                            {canEditCollections ? (
                                                <button
                                                    onClick={() => handleToggle(row.id, 'isCollected', row.isCollected)}
                                                    className={`transition-colors ${row.isCollected ? 'text-primary-500 hover:text-primary-600' : 'text-dark-muted hover:text-dark-text'}`}
                                                    title="Tahsil Edildi İşaretle"
                                                >
                                                    {row.isCollected ? <CheckCircle2 size={24} className="md:w-6 md:h-6" /> : <Circle size={24} className="md:w-6 md:h-6" />}
                                                </button>
                                            ) : (
                                                row.isCollected ? <CheckCircle2 size={24} className="mx-auto text-primary-500 md:w-6 md:h-6" /> : <Circle size={24} className="mx-auto text-dark-muted md:w-6 md:h-6" />
                                            )}
                                        </td>

                                        {/* MUHASEBE DURUMU */}
                                        <td className="px-3 md:px-6 py-4 text-center hidden md:table-cell">
                                            {canEditCollections ? (
                                                <button
                                                    onClick={() => handleToggle(row.id, 'isAccounted', row.isAccounted)}
                                                    className={`transition-colors flex justify-center w-full ${row.isAccounted ? 'text-blue-500 hover:text-blue-600' : 'text-dark-muted hover:text-dark-text'}`}
                                                    title="Muhasebe Programına İşlendi"
                                                >
                                                    {row.isAccounted ? <CheckCircle2 size={24} /> : <Circle size={24} />}
                                                </button>
                                            ) : (
                                                <div className="flex justify-center w-full">
                                                    {row.isAccounted ? <CheckCircle2 size={24} className="text-blue-500" /> : <Circle size={24} className="text-dark-muted" />}
                                                </div>
                                            )}
                                        </td>

                                        {/* TARİH */}
                                        <td className="px-3 md:px-6 py-4 hidden lg:table-cell text-dark-muted font-medium text-xs">
                                            <div>{formatRecordDate(row.createdAt)}</div>
                                            <div className="mt-1 text-[11px] text-dark-muted/70">{formatRecordTime(row.createdAt)}</div>
                                        </td>

                                        {/* İŞLEMLER */}
                                        <td className="px-3 md:px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-1 md:gap-2">
                                                {row.imageUrl && (
                                                    <button
                                                        onClick={() => setViewImage(process.env.NODE_ENV === 'production' ? row.imageUrl : `http://localhost:5000${row.imageUrl}`)}
                                                        className="p-1 md:p-2 text-primary-500 hover:text-primary-600 hover:bg-primary-500/10 rounded-lg transition-colors"
                                                        title="Dekont / Makbuz Gör"
                                                    >
                                                        <ImageIcon size={18} className="md:w-5 md:h-5" />
                                                    </button>
                                                )}
                                                {canEditCollections && (
                                                    <>
                                                        <button onClick={() => { setCollectionToEdit(row); setIsAddModalOpen(true); }} className="p-1 md:p-2 text-dark-muted hover:text-blue-500 hover:bg-blue-500/10 rounded-lg transition-colors" title="Detay / Düzenle">
                                                            <Pencil size={18} className="md:w-5 md:h-5" />
                                                        </button>
                                                        <button onClick={() => handleDelete(row.id)} className="p-1 md:p-2 text-dark-muted hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors" title="Sil">
                                                            <Trash2 size={18} className="md:w-5 md:h-5" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* YÜKLENİYOR / DAHA FAZLA (Sonsuz Kaydırma İçin) */}
                {pagination.page < pagination.totalPages && (
                    <div className="flex flex-col items-center justify-center p-6 border-t border-dark-border bg-dark-bg/30">
                        {isLoading ? (
                            <div className="flex items-center gap-3 text-dark-muted font-medium">
                                <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                                <span>Daha fazla kayıt yükleniyor...</span>
                            </div>
                        ) : (
                            <button
                                onClick={() => fetchCollections(pagination.page + 1, true)}
                                className="text-sm font-semibold text-primary-500 hover:text-primary-600 px-6 py-2 rounded-lg bg-primary-500/10 hover:bg-primary-500/20 transition-colors shadow-sm"
                            >
                                Manuel Olarak Daha Fazla Yükle ({pagination.total - collections.length} kayıt kaldı)
                            </button>
                        )}
                    </div>
                )}
                {pagination.page >= pagination.totalPages && collections.length > 0 && (
                    <div className="text-center p-4 text-xs font-semibold text-dark-muted border-t border-dark-border bg-dark-bg/30">
                        Belirtilen tarihler veya filtrelere uyan tüm kayıtlar listelendi ({collections.length} adet)
                    </div>
                )}

                {/* Infinite scroll sentinel */}
                <div ref={scrollSentinelRef} className="h-1" />
            </div>

            {/* FOTOĞRAF GÖRÜNTÜLEME MODALI */}
            <ImageModal
                isOpen={!!viewImage}
                imageUrl={viewImage}
                onClose={() => setViewImage(null)}
            />

        </Layout>
    );
};

export default Collections;
